import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

// ─── Shared sub-components ─────────────────────────────────────────────────────

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative inline-flex items-center flex-shrink-0 cursor-pointer focus:outline-none transition-colors duration-300"
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        backgroundColor: checked ? '#F7941D' : 'rgba(255,255,255,0.12)',
      }}
    >
      <span
        className="inline-block rounded-full bg-white shadow transition-transform duration-300"
        style={{
          width: 18,
          height: 18,
          transform: checked ? 'translateX(22px)' : 'translateX(3px)',
        }}
      />
    </button>
  );
}

function StarRating({ value, onChange, readonly = false }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => {
        const filled = readonly ? star <= value : star <= (hovered || value);
        return (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            className="focus:outline-none transition-colors duration-150"
            style={{
              color: filled ? '#F7941D' : 'rgba(255,255,255,0.2)',
              cursor: readonly ? 'default' : 'pointer',
              fontSize: readonly ? 14 : 22,
              lineHeight: 1,
            }}
          >
            ★
          </button>
        );
      })}
    </div>
  );
}

// ─── Inline testimonial form (add & edit) ──────────────────────────────────────

const EMPTY_FORM = { quote: '', author: '', author_info: '', stars: 5, visible: true };

function TestimonialForm({ initial = EMPTY_FORM, onSave, onCancel, saving, saveStatus }) {
  const [form, setForm] = useState(initial);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div
      className="rounded-2xl flex flex-col gap-5 p-6 mt-2"
      style={{ backgroundColor: '#252525', border: '1px solid rgba(247,148,29,0.25)' }}
    >
      {/* Author */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Author</label>
        <input
          type="text"
          value={form.author}
          onChange={e => set('author', e.target.value)}
          placeholder="e.g. Sarah M."
          className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
          style={{
            backgroundColor: '#2A2A2A',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        />
      </div>

      {/* Author Title/Info */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Author Title/Info</label>
        <input
          type="text"
          value={form.author_info}
          onChange={e => set('author_info', e.target.value)}
          placeholder="e.g. Owner, Fort Orange Brewing"
          className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
          style={{
            backgroundColor: '#2A2A2A',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        />
      </div>

      {/* Quote */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Quote</label>
        <textarea
          rows={3}
          value={form.quote}
          onChange={e => set('quote', e.target.value)}
          placeholder="What did they say about Trivia Nights Live?"
          className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 resize-none focus:outline-none transition-colors duration-200"
          style={{
            backgroundColor: '#2A2A2A',
            border: '1px solid rgba(255,255,255,0.1)',
            lineHeight: 1.6,
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        />
      </div>

      {/* Star stars */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Star Rating</label>
        <StarRating value={form.stars} onChange={val => set('stars', val)} />
      </div>

      {/* Visible toggle */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-poppins font-semibold text-white text-sm">Show on site</p>
          <p className="font-poppins text-white/40 text-xs mt-0.5">
            Hide this testimonial without deleting it.
          </p>
        </div>
        <Toggle checked={form.visible} onChange={val => set('visible', val)} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={saving}
          className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: saving ? 'rgba(247,148,29,0.4)' : '#F7941D',
            color: '#1A1A1A',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.backgroundColor = '#ffb83f'; }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.backgroundColor = '#F7941D'; }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.6)',
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          Cancel
        </button>

        {saveStatus === 'success' && (
          <div className="flex items-center gap-1.5">
            <span style={{ color: '#4ade80' }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>Saved!</p>
          </div>
        )}
        {saveStatus === 'error' && (
          <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
            Something went wrong.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Single testimonial card ───────────────────────────────────────────────────

function TestimonialCard({ item, onVisibleToggle, onEdit, onDelete }) {
  const preview = item.quote.length > 80 ? item.quote.slice(0, 80) + '…' : item.quote;

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-3 transition-all duration-200"
      style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* Top row: quote preview + visible toggle */}
      <div className="flex items-start justify-between gap-3">
        <p
          className="font-poppins text-sm flex-1 min-w-0"
          style={{ color: item.visible ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}
        >
          "{preview}"
        </p>
        <Toggle checked={item.visible} onChange={val => onVisibleToggle(item.id, val)} />
      </div>

      {/* Author + stars */}
      <div className="flex items-center justify-between gap-2">
        <p className="font-poppins text-xs font-semibold" style={{ color: '#F7941D' }}>
          — {item.author || 'Unknown'}
        </p>
        <StarRating value={item.stars ?? 5} readonly />
      </div>

      {/* Action buttons */}
      <div
        className="flex items-center gap-2 pt-1"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(247,148,29,0.1)', color: '#F7941D', border: '1px solid rgba(247,148,29,0.2)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.1)'; }}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.15)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.18)'; e.currentTarget.style.color = '#f87171'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; e.currentTarget.style.color = 'rgba(248,113,113,0.7)'; }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Delete confirmation modal ─────────────────────────────────────────────────

function DeleteModal({ onConfirm, onCancel, deleting }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
    >
      <div
        className="rounded-2xl p-7 flex flex-col gap-5 w-full max-w-sm"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="flex flex-col gap-2">
          <h2 className="font-poppins font-bold text-white text-lg">Delete testimonial?</h2>
          <p className="font-poppins text-white/50 text-sm leading-relaxed">
            This will permanently remove the testimonial from your site.{' '}
            <span className="text-white/80 font-semibold">This cannot be undone.</span>
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{
              backgroundColor: deleting ? 'rgba(248,113,113,0.4)' : '#f87171',
              color: '#fff',
              cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, delete it'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────────────────

function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading]           = useState(true);

  // Which card is open for editing: null | 'new' | <id>
  const [editingId, setEditingId]       = useState(null);
  const [editInitial, setEditInitial]   = useState(EMPTY_FORM);

  const [saving, setSaving]             = useState(false);
  const [saveStatus, setSaveStatus]     = useState(null);

  // Delete confirmation
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting]             = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    const { data, error } = await supabase
      .from('testimonials')
      .select('*');
    if (!error && data) setTestimonials(data);
    setLoading(false);
  }

  // ── Visible quick-toggle (no form needed) ──────────────────────────────────
  async function handleVisibleToggle(id, newVal) {
    setTestimonials(prev =>
      prev.map(t => t.id === id ? { ...t, visible: newVal } : t)
    );
    await supabase.from('testimonials').update({ visible: newVal }).eq('id', id);
  }

  // ── Open edit form ─────────────────────────────────────────────────────────
  function handleEdit(item) {
    setEditInitial({ quote: item.quote, author: item.author, author_info: item.author_info ?? '', stars: item.stars ?? 5, visible: item.visible });
    setEditingId(item.id);
    setSaveStatus(null);
  }

  function handleAddNew() {
    setEditInitial(EMPTY_FORM);
    setEditingId('new');
    setSaveStatus(null);
  }

  function handleCancel() {
    setEditingId(null);
    setSaveStatus(null);
  }

  // ── Save (insert or update) ────────────────────────────────────────────────
  async function handleSave(form) {
    if (!form.quote.trim() || !form.author.trim()) {
      setSaveStatus('error');
      return;
    }
    setSaving(true);
    setSaveStatus(null);

    let error;

    if (editingId === 'new') {
      const { error: e } = await supabase.from('testimonials').insert(form);
      error = e;
    } else {
      const { error: e } = await supabase
        .from('testimonials')
        .update(form)
        .eq('id', editingId);
      error = e;
    }

    setSaving(false);

    if (error) {
      setSaveStatus('error');
    } else {
      setSaveStatus('success');
      await fetchAll();
      setTimeout(() => {
        setEditingId(null);
        setSaveStatus(null);
      }, 1200);
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  async function handleDeleteConfirm() {
    setDeleting(true);
    const { error } = await supabase
      .from('testimonials')
      .delete()
      .eq('id', deleteTargetId);
    setDeleting(false);
    if (!error) {
      setTestimonials(prev => prev.filter(t => t.id !== deleteTargetId));
      if (editingId === deleteTargetId) setEditingId(null);
    }
    setDeleteTargetId(null);
  }

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="h-7 w-48 rounded-lg bg-white/10" />
        {[1, 2, 3].map(i => (
          <div key={i} className="h-28 rounded-2xl bg-white/5" />
        ))}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">

      {/* Header + Add button */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-poppins font-bold text-white text-2xl">Testimonials</h1>
          <p className="font-poppins text-white/40 text-sm mt-1">
            {testimonials.length} testimonial{testimonials.length !== 1 ? 's' : ''} — toggle the switch on any card to show or hide it instantly.
          </p>
        </div>
        {editingId !== 'new' && (
          <button
            type="button"
            onClick={handleAddNew}
            className="font-poppins font-bold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 focus:outline-none flex items-center gap-2 flex-shrink-0"
            style={{ backgroundColor: '#F7941D', color: '#1A1A1A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ffb83f'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F7941D'}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            Add New Testimonial
          </button>
        )}
      </div>

      {/* New testimonial form (shown above the list) */}
      {editingId === 'new' && (
        <TestimonialForm
          initial={editInitial}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          saveStatus={saveStatus}
        />
      )}

      {/* List */}
      {testimonials.length === 0 && editingId !== 'new' ? (
        <div
          className="rounded-2xl p-10 flex flex-col items-center justify-center gap-3"
          style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="font-poppins text-white/30 text-sm text-center">
            No testimonials yet. Hit "Add New Testimonial" to get started!
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {testimonials.map(item => (
            <div key={item.id}>
              <TestimonialCard
                item={item}
                onVisibleToggle={handleVisibleToggle}
                onEdit={handleEdit}
                onDelete={id => setDeleteTargetId(id)}
              />
              {/* Inline edit form directly below the card */}
              {editingId === item.id && (
                <TestimonialForm
                  initial={editInitial}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  saving={saving}
                  saveStatus={saveStatus}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTargetId && (
        <DeleteModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTargetId(null)}
          deleting={deleting}
        />
      )}

    </div>
  );
}

export default TestimonialsSection;
